import { useRef } from 'react'
import { Button } from '../ui/Button'
import { Card } from '../ui/Card'
import { X, Printer, FileText } from 'lucide-react'
import { GSTInvoice } from '../billing/GSTInvoice'

interface CartItem {
  id: string
  name: string
  originalPrice: number
  price: number
  quantity: number
  total: number
  gst_rate: number
  gst_type: 'inclusive' | 'exclusive'
  discount?: {
    type: 'fixed' | 'percentage'
    value: number
    amount: number
  }
  isWeighable?: boolean
  unit?: string
}

interface Customer {
  id: string
  name: string
  phone?: string
  email?: string
  gst_number?: string
}

interface BillPreviewModalProps {
  isOpen: boolean
  onClose: () => void
  cart: CartItem[]
  customer: Customer | null
  cartSubtotal: number
  orderDiscount?: { type: 'fixed' | 'percentage'; value: number } | null
  orderDiscountAmount: number
  taxRate: number
  taxAmount: number
  finalTotal: number
  paymentMethod: string
  formatCurrency: (amount: number) => string
}

export const BillPreviewModal = ({
  isOpen,
  onClose,
  cart,
  customer,
  cartSubtotal,
  orderDiscount,
  orderDiscountAmount,
  taxRate,
  taxAmount,
  finalTotal,
  paymentMethod,
  formatCurrency
}: BillPreviewModalProps) => {
  const printRef = useRef<HTMLDivElement>(null)

  const handlePrint = () => {
    if (printRef.current) {
      const printWindow = window.open('', '_blank')
      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head>
              <title>Bill Preview</title>
              <style>
                body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
                .print-content { max-width: 800px; margin: 0 auto; }
                .no-print { display: none !important; }
                @media print {
                  body { margin: 0; }
                  .no-print { display: none !important; }
                }
              </style>
            </head>
            <body>
              <div class="print-content">
                ${printRef.current.innerHTML}
              </div>
            </body>
          </html>
        `)
        printWindow.document.close()
        printWindow.print()
        printWindow.close()
      }
    }
  }

  if (!isOpen) return null

  // Generate invoice data for GSTInvoice component
  const invoiceData = {
    invoice_number: `INV-${Date.now()}`,
    invoice_date: new Date().toLocaleDateString('en-IN'),
    customer: {
      name: customer?.name || 'Walk-in Customer',
      phone: customer?.phone || '',
      email: customer?.email || '',
      address: '',
      gst_number: customer?.gst_number || '',
      state: 'Tamil Nadu',
      state_code: '33'
    },
    business: {
      name: 'Diwali Store',
      address: 'Shop No. 123, Main Street\nChennai, Tamil Nadu - 600001',
      gst_number: '33AAAAA0000A1Z5',
      phone: '+91 98765 43210',
      email: 'store@diwali.com',
      state: 'Tamil Nadu',
      state_code: '33'
    },
    items: cart.map(item => {
      let taxableValue, gstAmount
      
      if (item.gst_type === 'inclusive') {
        // For inclusive GST, extract the base amount and calculate tax
        taxableValue = (item.total * 100) / (100 + item.gst_rate)
        gstAmount = item.total - taxableValue
      } else {
        // For exclusive GST, the total is the base amount and tax is added
        taxableValue = item.total
        gstAmount = (item.total * item.gst_rate) / 100
      }

      return {
        id: item.id,
        product_name: item.name,
        hsn_code: '1234',
        quantity: item.quantity,
        unit_price: item.gst_type === 'inclusive' 
          ? (item.price * 100) / (100 + item.gst_rate) // Base price for inclusive
          : item.price, // Actual price for exclusive
        gst_rate: item.gst_rate,
        gst_type: item.gst_type,
        cgst_amount: gstAmount / 2, // Half of GST for CGST
        sgst_amount: gstAmount / 2, // Half of GST for SGST  
        igst_amount: 0, // For intra-state, IGST is 0
        total_amount: item.gst_type === 'inclusive' 
          ? item.total // For inclusive, total already includes tax
          : item.total + gstAmount // For exclusive, add tax to total
      }
    }),
    subtotal: cartSubtotal - orderDiscountAmount,
    total_cgst: taxAmount / 2,
    total_sgst: taxAmount / 2,
    total_igst: 0,
    total_gst: taxAmount,
    grand_total: finalTotal,
    payment_method: paymentMethod,
    round_off: 0
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-6xl max-h-[90vh] bg-white dark:bg-gray-800 flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Bill Preview
          </h3>
          <div className="flex items-center gap-2">
            <Button
              onClick={handlePrint}
              size="sm"
              className="flex items-center gap-2"
            >
              <Printer className="h-4 w-4" />
              Print
            </Button>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <div ref={printRef}>
            <GSTInvoice data={invoiceData} />
          </div>
        </div>

        <div className="flex justify-end gap-2 p-4 border-t border-gray-200 dark:border-gray-700 no-print flex-shrink-0">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button onClick={handlePrint} className="flex items-center gap-2">
            <Printer className="h-4 w-4" />
            Print Bill
          </Button>
        </div>
      </Card>
    </div>
  )
}